/**
 * PATH: packages/ui/src/kanban/components/MentionTextarea.tsx
 * FILE_ID: UI-KANBAN-MENTIONTEXTAREA-001
 * PURPOSE: One reusable `@mention`-aware textarea for every free-text
 *          surface in the Kanban drawer — card Description and the
 *          Comments composer — so the autocomplete, keyboard nav, and
 *          highlight all behave identically and live in one place.
 * WHY:
 *   The Comments section shipped a full inline @mention autocomplete,
 *   but the Description right above it was a plain <textarea> — so
 *   typing "@mark" in a description did nothing (operator complaint
 *   2026-06-10: "can't tag people here"). Rather than copy the comment
 *   logic, both now mount this component, which is built on the pure,
 *   tested engine in ``mentionTokens.ts`` (detectActiveMention /
 *   buildMemberSuggestions / applyMention / deriveMemberMentionIds).
 *
 * CONTRACT:
 *   - Controlled: parent owns ``value``.
 *   - ``onChange(value, mentionIds)`` fires on every edit with the
 *     CURRENT set of tagged member ids derived from the text — the
 *     parent forwards ``mentionIds`` to the save call so the backend
 *     can fire mention notifications.
 *   - Member-only by design: ``@today`` etc. are NOT date tokens here
 *     (that is the quick-add title composer's job). ``@here`` / ``@all``
 *     simply don't match a member, so they're left as literal text for
 *     the backend's broadcast handler.
 *   - Adaptive placement: the suggestion list opens DOWN by default and
 *     flips UP only when there's no room below. Operator report
 *     2026-06-10 ("look how ATT cut off") — the list used to always open
 *     UP (``bottom-full``) and the card drawer's sticky header / offline
 *     banner sliced the top row off. ``chooseMentionPlacement`` measures
 *     the room inside the nearest scroll container and picks the side
 *     that won't clip.
 */
import { AtSign } from 'lucide-react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { KanbanMember } from '../api'
import {
  applyMention,
  buildMemberSuggestions,
  deriveMemberMentionIds,
  detectActiveMention,
  type ActiveMention,
  type MentionSuggestion,
} from '../mentionTokens'

export interface MentionTextareaProps {
  value: string
  /** Fired on every edit with the text + the member ids tagged in it. */
  onChange: (value: string, mentionIds: number[]) => void
  members: KanbanMember[]
  placeholder?: string
  rows?: number
  disabled?: boolean
  className?: string
  /** Fired on Cmd/Ctrl+Enter (used by the Comments composer to post). */
  onSubmit?: () => void
  onBlur?: () => void
  autoFocus?: boolean
  ariaLabel?: string
}

/** Pixel height we'd LIKE the dropdown to have (matches `max-h-56` =
 *  14rem = 224px). Used to decide whether the space below the caret can
 *  hold the list without the drawer's scroll container clipping it. */
const MENTION_DROPDOWN_PX = 224

/**
 * Decide whether the suggestion list should open below ('bottom') or
 * above ('top') the textarea. Pure I → O so it's unit-testable without
 * a DOM. Rule: prefer opening DOWNWARD (matches caret reading order and
 * avoids the card drawer's sticky header / offline banner slicing the
 * top row off — the operator-reported "ATT cut off" glitch). Flip up
 * only when below genuinely can't hold the list AND above has more room.
 */
export function chooseMentionPlacement(
  spaceAbove: number,
  spaceBelow: number,
  desiredPx: number = MENTION_DROPDOWN_PX,
): 'top' | 'bottom' {
  if (spaceBelow >= desiredPx) return 'bottom'
  if (spaceAbove > spaceBelow) return 'top'
  return 'bottom'
}

/**
 * Measure the room above/below the textarea WITHIN the nearest
 * scrollable ancestor (the drawer body) — that's the surface that
 * actually clips an absolutely-positioned child. Falls back to the
 * viewport when there's no clipping ancestor. Defensive: any DOM gap
 * (SSR / detached node) yields a "lots of room below" default so we
 * never crash the composer.
 */
function measureMentionSpaces(ta: HTMLTextAreaElement | null): {
  above: number
  below: number
} {
  if (!ta || typeof window === 'undefined') {
    return { above: 0, below: MENTION_DROPDOWN_PX }
  }
  const rect = ta.getBoundingClientRect()
  let clipTop = 0
  let clipBottom = window.innerHeight
  let el: HTMLElement | null = ta.parentElement
  while (el) {
    const oy = window.getComputedStyle(el).overflowY
    if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
      const r = el.getBoundingClientRect()
      clipTop = Math.max(clipTop, r.top)
      clipBottom = Math.min(clipBottom, r.bottom)
      break
    }
    el = el.parentElement
  }
  return { above: rect.top - clipTop, below: clipBottom - rect.bottom }
}

/**
 * Dropdown of member suggestions. Purely presentational — the textarea
 * owns the keyboard so this just renders + reports clicks.
 */
function SuggestionList({
  items,
  activeIndex,
  onPick,
  placement,
}: {
  items: MentionSuggestion[]
  activeIndex: number
  onPick: (s: MentionSuggestion) => void
  placement: 'top' | 'bottom'
}) {
  // BUG-2026-06-29-KANBAN-MENTION-NO-SCROLL — arrow-key nav moved the
  // highlight but never scrolled the list, so once the active option fell
  // past the `max-h-56` fold it vanished and the list felt stuck ("can't go
  // down … list doesn't scroll"). Keep the active option scrolled into view.
  const activeRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])
  if (items.length === 0) return null
  return (
    <div
      role="listbox"
      className={
        'absolute left-0 z-20 max-h-56 w-full overflow-auto rounded-md border border-neutral-200 bg-white text-xs shadow-lg dark:border-neutral-800 dark:bg-neutral-900 ' +
        (placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1')
      }
    >
      {items.map((s, i) => (
        <button
          key={s.key}
          type="button"
          role="option"
          ref={i === activeIndex ? activeRef : undefined}
          aria-selected={i === activeIndex}
          onMouseDown={(e) => {
            // Keep focus in the textarea; fire before the blur timer.
            e.preventDefault()
            onPick(s)
          }}
          className={
            'flex w-full items-center gap-2 px-2 py-1.5 text-left ' +
            (i === activeIndex
              ? 'bg-blue-50 dark:bg-blue-900/30'
              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800')
          }
        >
          <AtSign className="h-3 w-3 shrink-0 text-neutral-400" />
          <span className="truncate font-medium text-neutral-900 dark:text-neutral-100">
            {s.label}
          </span>
          {s.detail && (
            <span className="ml-auto truncate text-[10px] text-neutral-400">
              {s.detail}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

export const MentionTextarea = forwardRef<
  HTMLTextAreaElement,
  MentionTextareaProps
>(function MentionTextarea(
  {
    value,
    onChange,
    members,
    placeholder,
    rows = 3,
    disabled,
    className,
    onSubmit,
    onBlur,
    autoFocus,
    ariaLabel,
  },
  forwardedRef,
) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  useImperativeHandle(forwardedRef, () => taRef.current as HTMLTextAreaElement)

  const [active, setActive] = useState<ActiveMention | null>(null)
  const [show, setShow] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom')
  // Last `@query` we reset the highlight for. `recompute` runs on every
  // keyup (including ArrowUp/Down), so resetting activeIndex on each call
  // snapped the highlight back to row 0 right after an arrow press — the
  // "can't go down with the arrow key" bug. Only reset when the query text
  // itself changes. (BUG-2026-06-29-KANBAN-MENTION-ARROW)
  const lastQueryRef = useRef<string | null>(null)

  const suggestions = useMemo(
    () => (show && active ? buildMemberSuggestions(active.query, members) : []),
    [show, active, members],
  )

  const emit = useCallback(
    (next: string) => onChange(next, deriveMemberMentionIds(next, members)),
    [onChange, members],
  )

  /** Recompute the active `@token` under the caret from the live DOM. */
  const recompute = useCallback(() => {
    const ta = taRef.current
    if (!ta) {
      setShow(false)
      return
    }
    const found = detectActiveMention(ta.value, ta.selectionStart ?? 0)
    if (!found) {
      setShow(false)
      setActive(null)
      lastQueryRef.current = null
      return
    }
    setActive(found)
    setShow(true)
    if (lastQueryRef.current !== found.query) {
      lastQueryRef.current = found.query
      setActiveIndex(0)
    }
    // Open toward whichever side has room so the drawer's scroll
    // container can't clip the list (the "ATT cut off" glitch).
    const { above, below } = measureMentionSpaces(ta)
    setPlacement(chooseMentionPlacement(above, below))
  }, [])

  const accept = useCallback(
    (s: MentionSuggestion) => {
      const ta = taRef.current
      if (!ta || !active) return
      const { text, caret } = applyMention(ta.value, active, s)
      emit(text)
      setShow(false)
      setActive(null)
      requestAnimationFrame(() => {
        if (taRef.current) {
          taRef.current.focus()
          taRef.current.setSelectionRange(caret, caret)
        }
      })
    },
    [active, emit],
  )

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={className}
        onChange={(e) => {
          emit(e.target.value)
          recompute()
        }}
        onKeyUp={recompute}
        onClick={recompute}
        onBlur={() => {
          // Delay so a mousedown on a suggestion lands before we hide.
          window.setTimeout(() => setShow(false), 120)
          onBlur?.()
        }}
        onKeyDown={(e) => {
          if (show && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActiveIndex((i) => (i + 1) % suggestions.length)
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActiveIndex(
                (i) => (i - 1 + suggestions.length) % suggestions.length,
              )
              return
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault()
              accept(suggestions[activeIndex]!)
              return
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              setShow(false)
              return
            }
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) {
            e.preventDefault()
            onSubmit()
          }
        }}
      />
      {show && suggestions.length > 0 && (
        <SuggestionList
          items={suggestions}
          activeIndex={activeIndex}
          onPick={accept}
          placement={placement}
        />
      )}
    </div>
  )
})

export default MentionTextarea
